import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Loading from '../ui/Loading';
import Alert from '../ui/Alert';
import Badge from '../ui/Badge';
import AlarmStatusBadge from './AlarmStatusBadge';
import { Bell, RefreshCw, Filter, ChevronLeft, ChevronRight, Image, Video } from 'lucide-react';
import { alarmService } from '../../services/alarm.service';
import { Alarm, AlarmFilters, ALARM_TYPE_NAMES } from '../../types/alarm.types';
import { useLocation } from '../../contexts/LocationContext';

interface AlarmListProps {
  locationId: number;
}

/**
 * AlarmList Component
 * 
 * Displays a paginated list of alarms for a location with filtering options
 */
const AlarmList: React.FC<AlarmListProps> = ({ locationId }) => {
  const { locations } = useLocation();
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AlarmFilters>({
    page: 1,
    page_size: 20,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAlarms(true); // Show loading on first load
    
    // Auto-refresh every 5 seconds (without loading state)
    const interval = setInterval(() => {
      fetchAlarms(false);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [locationId, filters.page, filters.alarm_type, filters.alarm_priority]);

  const fetchAlarms = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      setError(null);
      
      const response = await alarmService.getLocationAlarms(locationId, filters);
      
      setAlarms(response.data);
      setPagination(response.pagination);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch alarms';
      setError(errorMsg);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const handleFilterChange = (key: keyof AlarmFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString: string) => {
    // Use database timestamp as-is (already in correct local time)
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getAlarmTypeName = (code: string): string => {
    return ALARM_TYPE_NAMES[code] || code;
  };

  if (isLoading && alarms.length === 0) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-12">
          <Loading size="lg" text="Loading alarms..." />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="text-accent-primary" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Cloud Alarms</h3>
              <p className="text-sm text-text-muted">
                {pagination.total} total alarm{pagination.total !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Filter size={16} />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => fetchAlarms(true)}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert 
            variant="error" 
            message={error} 
            onClose={() => setError(null)} 
          />
        )}

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-surface-secondary rounded-lg">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">
                Alarm Type
              </label>
              <select
                value={filters.alarm_type || ''}
                onChange={(e) => handleFilterChange('alarm_type', e.target.value || undefined)}
                className="w-full px-3 py-2 bg-surface-primary border border-surface-secondary rounded-lg text-text-primary focus:ring-2 focus:ring-accent-primary focus:border-transparent"
              >
                <option value="">All Types</option>
                {Object.entries(ALARM_TYPE_NAMES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">
                Priority
              </label>
              <select
                value={filters.alarm_priority || ''}
                onChange={(e) => handleFilterChange('alarm_priority', e.target.value || undefined)}
                className="w-full px-3 py-2 bg-surface-primary border border-surface-secondary rounded-lg text-text-primary focus:ring-2 focus:ring-accent-primary focus:border-transparent"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ page: 1, page_size: 20 });
                  setShowFilters(false);
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {/* Alarm List */}
        {alarms.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto text-text-muted mb-3" size={48} />
            <p className="text-text-muted">No alarms found</p>
            {(filters.alarm_type || filters.alarm_priority) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ page: 1, page_size: 20 })}
                className="mt-3"
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {alarms.map((alarm) => (
              <div
                key={alarm.event_id}
                className="p-4 bg-surface-secondary rounded-lg border border-surface-hover hover:border-accent-primary transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <AlarmStatusBadge priority={alarm.alarm_priority} size="sm" />
                      <Badge variant="info" className="text-xs">
                        {getAlarmTypeName(alarm.alarm_type)}
                      </Badge>
                      {alarm.acknowledged && (
                        <Badge variant="success" className="text-xs">
                          Acknowledged
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-text-muted">Camera:</span>
                        <span className="ml-2 text-text-primary font-medium">
                          {alarm.event_camera}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted">Device:</span>
                        <span className="ml-2 text-text-primary font-mono text-xs">
                          {alarm.cloud_device_id.substring(0, 20)}...
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted">Time:</span>
                        <span className="ml-2 text-text-primary">
                          {formatDate(alarm.created_date)}
                        </span>
                      </div>
                    </div>

                    {alarm.event_note && (
                      <p className="mt-2 text-sm text-text-muted italic">
                        {alarm.event_note}
                      </p>
                    )}

                    {/* Snapshot preview */}
                    {alarm.event_snapshot_url && (
                      <div className="mt-3">
                        <img
                          src={alarm.event_snapshot_url}
                          alt="Alarm snapshot"
                          className="rounded-lg border border-surface-hover max-w-xs cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(alarm.event_snapshot_url, '_blank')}
                          onError={(e) => {
                            // Hide image if it fails to load
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {alarm.event_video_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(alarm.event_video_url, '_blank')}
                      >
                        View Video
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/events/${alarm.event_id}`, '_blank')}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-surface-secondary">
            <p className="text-sm text-text-muted">
              Showing {((pagination.page - 1) * pagination.page_size) + 1} to{' '}
              {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
              {pagination.total} alarms
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ChevronLeft size={16} />}
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || isLoading}
              >
                Previous
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isLoading}
                      className={`
                        w-8 h-8 rounded-lg text-sm font-medium transition-colors
                        ${pageNum === pagination.page
                          ? 'bg-accent-primary text-white'
                          : 'bg-surface-secondary text-text-primary hover:bg-surface-hover'
                        }
                      `}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ChevronRight size={16} />}
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AlarmList;
